import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BsPencilSquare, BsTrash } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import client from 'api/WookAxios';
import log from 'loglevel';
import palette from 'lib/styles/palette';
import userImage from 'images/user.png';
import { CommentReactions } from './CommentReactions';

const CommentItemBlock = styled.div`
`;

const CommentContainer = styled.div`
  /* background: lightgray; */
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 15px;
`;

const UserImage = styled.div`
  box-sizing: border-box;
  width: ${({ $compact }) => $compact ? '32px' : '40px'};
  margin-right: 10px;
`;

const UserURLImage = styled.div`
  box-sizing: border-box;
  border-radius: 50%;
  width: ${({ $compact }) => $compact ? '32px' : '40px'};
  height: ${({ $compact }) => $compact ? '32px' : '40px'};
  margin-right: 10px;
`;

const CommentLine = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 3px;
`;

const SubInfoBlock = styled.div`
  margin-top: 5px;
  font-size: 13px;
  color: ${palette.gray[6]};

  span + span:before {
    color: ${palette.gray[4]};
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    content: '\\B7';
  }
`;

const Author = styled.label`
  color: hotpink;
  font-weight: 600;
`;

const CommentContent = styled.div`
  margin-right: 20px;
  margin-top: 2px;
  font-size: 16px;
`;

const DeletedComment = styled.div`
  margin-right: 20px;
  margin-top: 2px;
  font-size: 14px;
  font-style: italic;
  color: ${palette.gray[5]};
  font-weight: 550;
`;

const CommentEdit = styled.div`
  width: 20px;
  cursor: pointer;
  margin-left: 20px;

  &:hover {
    background: lightgray;
  }
`;

const CommentDelete = styled.div`
  width: 20px;
  cursor: pointer;
  margin-left: 20px;

  &:hover {
    background: lightgray;
  }
`;

const CommentActions = styled.div`
  /* background: lightblue; */
  display: flex;
  align-items: center;
  margin-left: 50px;
  margin-bottom: 3px;
  font-size: 12px;
`;

const CommentReactionsBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 3px;
`;

const ReplyButton = styled.button`
  margin-left: 12px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    background: lightgray;
  }
`;

const ReplyPost = styled.div`
  display: flex;
  align-items: center;
  margin-left: 55px;
  margin-top: 5px;
`;

const ReplyInput = styled.textarea`
  width: 330px;
  margin-right: 10px;
  padding-left: 5px;
  border: none;
  border-bottom: 1px solid ${palette.gray[4]};
  outline: none;
  resize: none;
  box-sizing: border-box;
  font-size: 15px;
  font-family: "Noto Sans KR";
`;

const ReplyTo = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: BlueViolet;
`;

const CommentButton = styled.button`
  margin-top: 2px;
  margin-bottom: 8px;
  margin-left: 0px;
  padding: 4px 5px;
  border: none;
  border-radius: 6px;
  background: none;
  font-size: 15px;
  font-weight: 550;
  color: MediumPurple;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    background: lightgray;
  }
`;

const ChildrenBlock = styled.div`
  margin-left: 20px;
  padding-left: 20px;
  border-left: 1px solid ${palette.gray[3]};
`;

const CommentEditInput = styled.textarea`
  margin-right: 10px;
  padding-left: 5px;
  width: 330px;
  border: none;
  border-bottom: 1px solid ${palette.gray[4]};
  outline: none;
  box-sizing: border-box;
  font-size: 16px;
  font-family: "Noto Sans KR";

  @media (max-width: 550px) {
    width: 60%;
  }

  @media (max-width: 400px) {
    width: 50%;
  }
`;

const ActionButton = styled.button`
  padding: 0.25rem;
  border-radius: 4px;
  color: ${palette.gray[6]};
  font-weight: bold;
  border: none;
  outline: none;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background: ${palette.gray[1]};
    color: hotpink;
  }

  & + & {
    margin-left: 0.25rem;
  }
`;

const timeDisplayFormat = {
  hour12: false,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

export const CommentItem = ({
  comment,
  user,
  postId,
  onAppendSibling,
  onUpdateSelf,
  onRemoveSelf,
}) => {
  const [editing, setEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');

  const [replyVisible, setReplyVisible] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  const [children, setChildren] = useState([]);
  const [nextChildrenPage, setNextChildrenPage] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const replyInputRef = useRef(null);

  useEffect(() => {
    if (replyVisible) {
      replyInputRef.current?.focus();
    }
  }, [replyVisible]);

  const handleEdit = () => {
    setEditing(true);
    setEditingContent(comment.content);
  };

  const initializeEdit = () => {
    setEditing(false);
    setEditingContent('');
  };

  const handleToggleChildren = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    if (children.length === 0 && comment.children_count > 0) {
      try {
        const response = await client.getCommentChildren(comment.id);
        log.debug('GET_COMMENT_CHILDREN_SUCCESS', response);
        setChildren(response.data.results);
        setNextChildrenPage(response.data.next);
      } catch (e) {
        log.debug('GET_COMMENT_CHILDREN_FAILED', e);
        return;
      }
    }
    
    setExpanded(true);
  };

  const handleLoadMoreComments = async () => {
    if (!nextChildrenPage) return;

    try {
      const response = await client.getPostComments(postId, nextChildrenPage);
      log.debug('LOAD_MORE_COMMENTS_SUCCESS', response);

      setChildren(prev =>[
        ...prev,
        ...response.data.results,
      ]);
      setNextChildrenPage(response.data.next);
    } catch(e) {
      log.debug('LOAD_MORE_COMMENTS_FAILED', e);
    }
  };

  const handleCreateComment = async () => {
    if (!replyContent.trim()) return;

    const data = {
      content: replyContent,
      reply_to: comment.id,
    };

    try {
      const response = await client.createComment(postId, data);
      log.debug('COMMENT_REPLY_CREATE_SUCCESS', response);

      const createdComment = response.data;
      if (createdComment.parent === comment.id) {
        handleAppendChild(createdComment);
      } else {
        onAppendSibling(createdComment);
      }

      setReplyContent('');
      setReplyVisible(false);
    } catch (e) {
      log.debug('COMMENT_REPLY_CREATE_FAILED', e);
    }
  };

  const handleUpdateComment = async () => {
    const data = {
      content: editingContent,
    };

    try {
      const response = await client.updateComment(comment.id, data);
      log.debug('COMMENT_UPDATE_SUCCESS', response);
      
      const updatedComment = response.data;
      onUpdateSelf(updatedComment);
      
      initializeEdit();
    } catch (e) {
      log.debug('COMMENT_UPDATE_FAILED', e);
    }
  };

  const handleDeleteComment = async () => {
    const confirm = window.confirm('정말 삭제하시겠습니까?');
    if (!confirm) return;

    try {
      const response = await client.deleteComment(comment.id);
      log.debug('COMMENT_DELETE_SUCCESS', response);
      
      if (response.data.hard_deleted) {
        onRemoveSelf(comment);
      } else {
        const deletedComment = response.data.comment;
        onUpdateSelf(deletedComment);
      }
    } catch (e) {
      log.debug('COMMENT_DELETE_FAILED', e);
    }
  };

  const handleAppendChild = createdComment => {
    setChildren(prev => [...prev, createdComment]);
    setExpanded(true);

    onUpdateSelf({
      ...comment,
      children_count: comment.children_count + 1,
    });
  };

  const handleUpdateChild = updatedComment => {
    setChildren(prev =>
      prev.map(comment =>
        comment.id === updatedComment.id
          ? { ...comment, ...updatedComment }
          : comment
      )
    );
  };

  const handleRemoveChild = deletedComment => {
    setChildren(prev =>
      prev.filter(comment =>
        comment.id !== deletedComment.id
      )
    );

    onUpdateSelf({
      ...comment,
      children_count: comment.children_count - 1,
    });
  };
    
  return (
    <CommentItemBlock>
      <CommentContainer>
        {comment.profile_url ? (
          <UserURLImage $compact={comment.depth > 1}>
            <img src={comment.profile_url} alt="url-image" width="100%" style={{ borderRadius: '50%' }} />
          </UserURLImage>
        ) : (
          <UserImage $compact={comment.depth > 1}>
            <img src={userImage} alt="user-image" width="100%"/>
          </UserImage>
        )}

        <CommentLine>
          <SubInfoBlock>
            <span><Author>{comment.author_name}</Author></span>
            <span>{new Date(comment.created_at).toLocaleString('ko-KR', timeDisplayFormat)}</span>
            {comment.edited_at && <span>수정됨</span>}
          </SubInfoBlock>

          {editing ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CommentEditInput value={editingContent} onChange={e => setEditingContent(e.target.value)} />
              <div>
                <ActionButton onClick={handleUpdateComment}>저장</ActionButton>
                <ActionButton onClick={initializeEdit}>취소</ActionButton>
              </div>
            </div>
          ) : (
            <CommentContent>
              {comment.is_deleted ? (
                <DeletedComment>**삭제된 댓글**</DeletedComment>                
              ) : (
                <>
                  {comment.reply_to_author && (<ReplyTo>@{comment.reply_to_author} </ReplyTo>)}
                  {comment.content}
                </>
              )}
            </CommentContent>
          )}
        </CommentLine>

        {user.id === comment.author && (
          <>
            <CommentEdit><BsPencilSquare onClick={handleEdit} size="100%" /></CommentEdit>
            <CommentDelete><BsTrash onClick={handleDeleteComment} size="100%" /></CommentDelete>
          </>
        )}
      </CommentContainer>

      <CommentActions>
        <CommentReactionsBlock><CommentReactions user={user} comment={comment} /></CommentReactionsBlock>
        <ReplyButton onClick={() => setReplyVisible(prev => !prev)}>
          {replyVisible ? '취소' : '답글'}
        </ReplyButton>
      </CommentActions>

      {replyVisible && (
        <ReplyPost>
          <ReplyInput
            ref={replyInputRef}
            rows={1}
            value={replyContent}
            onChange={e => setReplyContent(e.target.value)}
            placeholder={`${comment.author_name}님에게 답글`}
          />
          <ActionButton onClick={handleCreateComment}>
            등록
          </ActionButton>
        </ReplyPost>
      )}

      {comment.children_count > 0 && (
        <ChildrenBlock>
          {comment.children_count > 0 && (
            <CommentButton onClick={handleToggleChildren}>
              {expanded ? '답글 숨기기 ▲' : `답글 ${comment.children_count}개 ▼`}
            </CommentButton>
          )}

          {expanded && (
            <>
              {children.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  user={user}
                  postId={postId}
                  onAppendSibling={handleAppendChild}
                  onUpdateSelf={handleUpdateChild}
                  onRemoveSelf={handleRemoveChild}
                />
              ))}

              {nextChildrenPage && (
                <CommentButton onClick={handleLoadMoreComments}>댓글 더보기 ▼</CommentButton>
              )}
            </>
          )}
        </ChildrenBlock>
      )}
    </CommentItemBlock>
  );
};